import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, Trash2 } from "lucide-react";
import { useCart } from "../context/CartContext";
import { formatINR, resolveImg } from "../lib/format";

export const CartSheet = () => {
  const { items, open, setOpen, setQty, remove, subtotal, count } = useCart();
  const nav = useNavigate();

  const goCheckout = () => { setOpen(false); nav("/checkout"); };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm"
          />
          <motion.aside
            data-testid="cart-sheet"
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="fixed top-0 right-0 z-[61] h-full w-full max-w-md bg-[#0b0b0b] border-l border-white/10 flex flex-col"
          >
            <div className="flex items-center justify-between px-6 py-6 border-b border-white/10">
              <div>
                <span className="overline text-[#B87333]">/ Your Cart</span>
                <h2 className="font-serif text-2xl mt-1">{count} item{count !== 1 ? "s" : ""}</h2>
              </div>
              <button data-testid="cart-close" onClick={() => setOpen(false)} className="grid place-items-center w-10 h-10 rounded-full border border-white/15 hover:border-[#B87333] transition-colors"><X size={18} /></button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {items.length === 0 ? (
                <div className="h-full grid place-items-center text-center">
                  <div>
                    <p className="font-body text-[#8A8A82]">Your cart is empty.</p>
                    <Link to="/shop" onClick={() => setOpen(false)} className="inline-block mt-4 text-[#B87333] font-grotesk text-xs uppercase tracking-[0.2em] hover:underline">Browse products</Link>
                  </div>
                </div>
              ) : (
                <div className="space-y-5">
                  {items.map((i) => (
                    <div key={i.key} data-testid={`cart-item-${i.key}`} className="flex gap-4">
                      <img src={resolveImg(i.image)} alt={i.name} className="w-20 h-20 object-cover rounded-sm border border-white/10" />
                      <div className="flex-1 min-w-0">
                        <div className="font-grotesk text-[10px] uppercase tracking-[0.18em] text-[#B87333]">{i.brand}</div>
                        <div className="font-serif text-base leading-tight">{i.name}</div>
                        <div className="font-body text-xs text-[#8A8A82] mt-0.5">{formatINR(i.price)} {i.unit}{i.diameter ? ` · ⌀ ${i.diameter} mm` : ""}</div>
                        <div className="flex items-center justify-between mt-2">
                          <div className="flex items-center border border-white/15 rounded-full">
                            <button data-testid={`cart-dec-${i.key}`} onClick={() => setQty(i.key, i.quantity - 1)} className="w-7 h-7 grid place-items-center hover:text-[#B87333]"><Minus size={12} /></button>
                            <input
                              data-testid={`cart-qty-${i.key}`}
                              type="number"
                              min={1}
                              value={i.quantity}
                              onChange={(e) => setQty(i.key, Math.max(1, Math.floor(Number(e.target.value) || 0)))}
                              onBlur={(e) => setQty(i.key, Math.max(i.moq || 1, Math.floor(Number(e.target.value) || 0)))}
                              className="w-10 text-center bg-transparent outline-none font-body text-sm text-[#F5F5F0] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            />
                            <button data-testid={`cart-inc-${i.key}`} onClick={() => setQty(i.key, i.quantity + 1)} className="w-7 h-7 grid place-items-center hover:text-[#B87333]"><Plus size={12} /></button>
                          </div>
                          <button data-testid={`cart-remove-${i.key}`} onClick={() => remove(i.key)} className="text-[#8A8A82] hover:text-[#C0392B] transition-colors"><Trash2 size={15} /></button>
                        </div>
                      </div>
                      <div className="font-serif text-sm whitespace-nowrap">{formatINR(i.price * i.quantity)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-white/10 px-6 py-6">
                <div className="flex items-center justify-between mb-1">
                  <span className="overline text-[#8A8A82]">Subtotal</span>
                  <span data-testid="cart-subtotal" className="font-serif text-2xl">{formatINR(subtotal)}</span>
                </div>
                <p className="font-body text-xs text-[#8A8A82] mb-5">Tier discount & GST calculated at checkout.</p>
                <button data-testid="cart-checkout" onClick={goCheckout} className="w-full rounded-full bg-[#F5F5F0] text-black py-4 font-grotesk text-xs uppercase tracking-[0.2em] hover:bg-[#B87333] transition-colors">
                  Proceed to checkout
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
